<template>
	<div
		class="omapi-campaigns-table"
		:class="{
			'omapi-campaigns-table-refreshing': isRefreshing,
			'omapi-campaigns-table-no-data': !hasCampaigns,
		}"
		@click="onTableClick"
	>
		<table-base :headers="headers" :striped="false" :is-loading="false" class="omapi-table">
			<template v-if="hasCampaigns">
				<campaigns-row
					v-for="campaign in campaigns"
					:key="campaign.id"
					:campaign-id="campaign.id"
					:class="`${rowClasses[campaign.id]}`"
					:row-visible="rowVisible(campaign)"
					:splits-open="splitsOpen(campaign)"
					@toggleAbTests="toggleAbTests"
				></campaigns-row>
			</template>

			<template #no-data>{{ noCampaignsError }}</template>
		</table-base>

		<div class="omapi-campaign-table__mods omapi-campaign-table__mods-bottom">
			<campaigns-table-bulk-actions
				:action-key="actionKey"
				:is-bulk="isBulk"
				@setAction="$emit('setAction', $event)"
			/>
			<campaigns-table-pagination />
		</div>

		<div v-for="campaign in campaigns" :key="campaign.id">
			<component :is="'campaigns-modal-' + modal" v-for="modal in modals" :key="modal" :campaign="campaign" />
		</div>

		<component :is="'campaigns-modal-' + modal" v-for="modal in bulkModals" :key="modal" />
	</div>
</template>

<script lang="js">
import { mapGetters, mapState, mapMutations, mapActions } from 'vuex';

const SORT_COLUMNS = {
	name: { asc: 'name-a-z', desc: 'name-z-a', bothDirs: true },
	visitors: { asc: 'visitors-asc', desc: 'visitors-desc', bothDirs: true },
	conversions: { asc: 'leads-asc', desc: 'leads-desc', bothDirs: true },
	cr: { asc: 'conversion-rate-asc', desc: 'conversion-rate-desc', bothDirs: true },
};
/**
 * Campaign Table
 *
 * @since 2.0.0
 */
export default {
	inheritAttrs: false,
	props: {
		/**
		 * Campaigns
		 */
		campaigns: {
			type: Array,
			default: () => [],
		},
		/**
		 * Whether we are in bulk edit mode.
		 */
		isBulk: Boolean,
		/**
		 * The Bulk Action key
		 */
		actionKey: {
			type: String,
			default: '',
		},
		/**
		 * Whether campaigns are being refreshed.
		 */
		isRefreshing: Boolean,
	},
	data() {
		return {
			modals: ['archive', 'delete', 'duplicate', 'preview', 'monster-link', 'split', 'trash'],
			bulkModals: ['bulk-archive', 'bulk-delete', 'bulk-trash'],
			abTestsToggled: [],
		};
	},
	computed: {
		...mapGetters(['hasSiteError']),
		...mapState('campaigns', ['bulk', 'sort', 'sortStack']),
		headers() {
			const GRAY = '#8A8F95';
			const BLUE = '#087CE1';
			const sortIcon = (col) => {
				const active = this.sortStack && this.sortStack.find((s) => s.column === col);
				const upColor = active && 'asc' === active.dir ? BLUE : GRAY;
				const downColor = active && 'desc' === active.dir ? BLUE : GRAY;
				return `<svg class="omapi-col-sort-icon" width="7" height="11" viewBox="0 0 7 11" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
					<path d="M3.5 0L7 4.5H0L3.5 0Z" fill="${upColor}"/>
					<path d="M3.5 11L0 6.5H7L3.5 11Z" fill="${downColor}"/>
				</svg>`;
			};
			return [
				{
					key: 'name',
					name: `<button type="button" class="omapi-col-sort-btn" data-sort="name">Name ${sortIcon('name')}</button>`,
					class: 'campaign-table-name',
				},
				{
					key: 'visitors',
					name: `<button type="button" class="omapi-col-sort-btn" data-sort="visitors">Visitors ${sortIcon('visitors')}</button>`,
					class: 'campaign-table-visitors',
				},
				{
					key: 'conversions',
					name: `<button type="button" class="omapi-col-sort-btn" data-sort="conversions">Conversions ${sortIcon('conversions')}</button>`,
					class: 'campaign-table-conversions',
				},
				{
					key: 'cr',
					name: `<button type="button" class="omapi-col-sort-btn" data-sort="cr">CR% ${sortIcon('cr')}</button>`,
					class: 'campaign-table-cr',
				},
				{ key: 'ab', name: 'A/B Tests', class: 'campaign-table-ab' },
				{ key: 'status', name: 'Status', class: 'campaign-table-status' },
				{ key: 'actions', name: '<span>Actions</span>', class: 'campaign-table-actions' },
			];
		},
		/**
		 * If there are any campaigns.
		 *
		 * @since  2.0.0
		 *
		 * @returns {boolean} If there are any campaigns.
		 */
		hasCampaigns() {
			return !this.hasSiteError && Boolean(this.campaigns && 0 < this.campaigns.length);
		},
		/**
		 * Object with a row class for every campaign row.
		 *
		 * @since  2.0.0
		 *
		 * @returns {boolean} Row classes object.
		 */
		rowClasses() {
			let lastStripe = '';
			const classes = {};
			this.campaigns.forEach((c) => {
				// If not a split test, then toggle the stripe color.
				if (!c.is_split) {
					lastStripe = 'alternate' === lastStripe ? '' : 'alternate';
				}

				classes[c.id] = lastStripe;
			});
			return classes;
		},
		noCampaignsError() {
			return this.hasSiteError ? 'An error was encountered.' : 'No campaign data.';
		},
	},
	methods: {
		...mapMutations('campaigns', ['updateSort', 'upsertColumnSort', 'removeColumnSort']),
		...mapActions('campaigns', ['refreshDashboard', 'setPageVars']),
		/**
		 * Capture clicks on sort buttons via event delegation.
		 *
		 * @param {Event} event Click event.
		 *
		 * @returns {void}
		 */
		onTableClick(event) {
			const btn = event.target.closest('[data-sort]');
			if (btn) {
				this.handleSort(btn.dataset.sort);
			}
		},
		/**
		 * Cycle a column through its sort states within the multi-sort stack.
		 * Three-state: inactive → asc (or desc for single-direction) → desc → inactive.
		 * First click adds the column to the stack (lowest priority among active sorts).
		 * Second click cycles direction in place (priority position unchanged).
		 * Third click removes the column from the stack.
		 *
		 * @param {string} column Sort column key.
		 *
		 * @returns {void}
		 */
		handleSort(column) {
			const config = SORT_COLUMNS[column];
			if (!config) {
				return;
			}

			const existing = this.sortStack.find((s) => s.column === column);
			let nextDir = null;

			if (!existing) {
				// Not yet in stack: start at asc if supported, else desc.
				nextDir = config.bothDirs ? 'asc' : 'desc';
			} else if ('asc' === existing.dir) {
				nextDir = 'desc';
			} else if ('desc' === existing.dir) {
				nextDir = null; // Third click removes from stack.
			}

			if (null === nextDir) {
				this.removeColumnSort(column);
			} else {
				const value = 'asc' === nextDir ? config.asc : config.desc;
				this.upsertColumnSort({ column, dir: nextDir, value });
			}

			this.setPageVars({ page: 1 });
			this.refreshDashboard().catch(() => {});
		},
		/**
		 * Determines if row is hidden, due to being a split test and not
		 * yet toggled open.
		 *
		 * @since  2.0.0
		 *
		 * @param {object} campaign Campaign object.
		 *
		 * @returns {boolean} Whether row is hidden.
		 */
		rowVisible(campaign) {
			return campaign.is_split ? this.abTestsToggled.includes(campaign.parent_id) : true;
		},
		/**
		 * Determines if campaign's split tests are open.
		 *
		 * @since  2.0.0
		 *
		 * @param {object} campaign Campaign object.
		 *
		 * @returns {boolean} Whether campaign's split tests are open.
		 */
		splitsOpen(campaign) {
			return this.abTestsToggled.includes(campaign.id);
		},
		/**
		 * Toggle the A/B tests for a campaign.
		 *
		 * @param {string} campaignId Campaign Id.
		 *
		 * @returns {void}
		 */
		toggleAbTests(campaignId) {
			const index = this.abTestsToggled.indexOf(campaignId);
			if (-1 !== index) {
				this.abTestsToggled.splice(index, 1);
			} else {
				this.abTestsToggled.push(campaignId);
			}
		},
	},
};
</script>

<style lang="scss">
@import '../../assets/scss/variables';
@import '../../assets/scss/components/campaign-table';
.omapi-campaigns-table {
	table.omapi-table {
		border: 0;

		thead th {
			border-color: #dddddd;
			padding-bottom: 12px;
			padding-top: 15px;
			color: #23282d;
			font-weight: 400;
		}

		.alternate {
			background-color: #f7f8fb;
		}
	}
	.stripe-gray {
		background-color: #f9f9f9;
	}
}
.omapi-campaigns-table-refreshing {
	opacity: 0.5;
	&.omapi-campaigns-table-no-data {
		td {
			padding-top: 34px;
			padding-bottom: 33px;
		}
	}
}
.omapi-campaign-table__mods .bulk-actions {
	display: flex;
	justify-content: center;
	align-items: center;
	margin-bottom: 12px;
	select {
		max-width: 168px;
		margin-right: 5px;
	}
	.omapi-dropdown__container {
		max-width: 168px;
	}
}
.omapi-campaign-table__mods-bottom .bulk-actions {
	margin-bottom: 0;
}

// Column sort header styles
.omapi-col-sort-btn {
	background: none;
	border: none;
	padding: 0;
	cursor: pointer;
	font-weight: 400;
	font-size: inherit;
	color: inherit;
	display: inline-flex;
	align-items: center;
	gap: 5px;
	white-space: nowrap;
	transition: color 0.12s ease;

	&:hover {
		color: $color_blue-primary;
	}
}
.omapi-col-sort-icon {
	display: inline-block;
	vertical-align: middle;
	flex-shrink: 0;
}
</style>
